import ProductCard from './ProductCard';
import type { Product } from '../utils/apiHelpers';

interface ProductGridProps {
  products: Product[];
  loading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  reducedSize?: boolean;
}

function ProductSkeleton() {
  return (
    <div className="rounded-lg shadow-md overflow-hidden bg-gray-100 dark:bg-gray-800 animate-pulse">
      <div className="h-48 md:h-64 bg-gray-200 dark:bg-gray-700" />
      <div className="p-4 space-y-2">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded mt-4" />
      </div>
    </div>
  );
}

export default function ProductGrid({
  products,
  loading = false,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  reducedSize = false,
}: ProductGridProps) {
  const gridClass = reducedSize 
    ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 sm:gap-6"
    : "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4 sm:gap-6";

  // Ensure products is always an array
  const productList = Array.isArray(products) ? products : [];

  if (loading && productList.length === 0) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span className="h-2.5 w-2.5 rounded-full bg-[#ff9100] animate-pulse" />
          <span>Loading products...</span>
        </div>
        <div className={gridClass}>
          {Array.from({ length: 8 }).map((_, i) => (
            <ProductSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (productList.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No products found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className={gridClass}>
        {productList.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
      {hasMore && onLoadMore && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loadingMore}
            className="px-6 py-3 bg-[var(--foreground)] text-[var(--background)] rounded-lg font-medium hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loadingMore ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
